import axios from 'axios';

const API_BASE_URL = '/api/forms';

// Configure axios defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for session-based auth
});

// Add a request interceptor to include the CSRF token
apiClient.interceptors.request.use((config) => {
  const csrfToken = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Error handler
const handleError = (error) => {
  console.error('API Error:', error);
  throw error.response?.data || { message: 'An error occurred' };
};

// Event Forms API
export const eventFormApi = {
  // Get or create form for an event
  getOrCreate: async (eventId) => {
    try {
      const response = await apiClient.get(`/event-forms/?event=${eventId}`);
      if (response.data.results && response.data.results.length > 0) {
        return response.data.results[0];
      }
      // If no form exists, create a new one
      return await eventFormApi.create({
        event: eventId,
        title: 'Event Registration Form',
        description: 'Please fill out this form to register for the event.',
      });
    } catch (error) {
      return handleError(error);
    }
  },

  // Get form by ID
  getById: async (formId) => {
    try {
      const response = await apiClient.get(`/event-forms/${formId}/`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Create a new form
  create: async (formData) => {
    try {
      const response = await apiClient.post('/event-forms/', formData);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Update a form
  update: async (formId, formData) => {
    try {
      const response = await apiClient.put(`/event-forms/${formId}/`, formData);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Publish a form
  publish: async (formId) => {
    try {
      const response = await apiClient.patch(`/event-forms/${formId}/`, { is_published: true });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
};

// Form Sections API
export const formSectionApi = {
  // Create a new section
  create: async (formId, sectionData) => {
    try {
      const response = await apiClient.post('/sections/', {
        ...sectionData,
        event_form: formId,
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Update a section
  update: async (sectionId, sectionData) => {
    try {
      const response = await apiClient.put(`/sections/${sectionId}/`, sectionData);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete a section
  delete: async (sectionId) => {
    try {
      await apiClient.delete(`/sections/${sectionId}/`);
      return { success: true };
    } catch (error) {
      return handleError(error);
    }
  },
};

// Form Questions API
export const formQuestionApi = {
  // Create a new question
  create: async (sectionId, questionData) => {
    try {
      const response = await apiClient.post('/questions/', {
        ...questionData,
        section: sectionId,
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Update a question
  update: async (questionId, questionData) => {
    try {
      const response = await apiClient.put(`/questions/${questionId}/`, questionData);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete a question
  delete: async (questionId) => {
    try {
      await apiClient.delete(`/questions/${questionId}/`);
      return { success: true };
    } catch (error) {
      return handleError(error);
    }
  },
};

// Form Responses API
export const formResponseApi = {
  // Submit a form response
  submit: async (formId, responseData) => {
    try {
      // First create the form response
      const response = await apiClient.post('/responses/', {
        event_form: formId,
        ...responseData,
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Get responses for a form
  getByForm: async (formId) => {
    try {
      const response = await apiClient.get(`/responses/?event_form=${formId}`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
};

// Question Responses API
export const questionResponseApi = {
  // Submit a question response
  submit: async (responseId, questionId, value) => {
    try {
      const response = await apiClient.post('/question-responses/', {
        form_response: responseId,
        question: questionId,
        value: value,
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
};

const formBuilderService = {
  eventForm: eventFormApi,
  section: formSectionApi,
  question: formQuestionApi,
  response: formResponseApi,
  questionResponse: questionResponseApi,
};

export default formBuilderService;
